<template>
  <div :class="dashboardContainerClass" id="dashboard-main-container">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4" id="dashboard-header-section">
      <div class="w-full" id="dashboard-welcome-banner">
        <div class="flex items-center justify-between">
          <div class="min-w-0">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white font-heebo" id="dashboard-welcome-message">{{ t('dashboard.welcomeMessage', { userName: userName }) }}</h1>
            <p class="text-gray-600 dark:text-gray-400 mt-2 text-lg" id="dashboard-current-datetime">{{ currentDateTime }}</p>
            <p v-if="false === true" class="text-gray-600 dark:text-gray-400 mt-1 text-base" id="dashboard-hebrew-date">{{ t('dashboard.hebrewDate', 'Hebrew date') }}: {{ currentHebrewDate }}</p>
          </div>
          <div class="hidden md:flex items-center" :class="isHebrew === true ? 'space-x-reverse space-x-3' : 'space-x-3'">
            <div class="relative w-72" id="dashboard-desktop-search-container">
              <input type="text" :placeholder="t('dashboard.searchAgentsPlaceholder')" :value="searchQuery" @input="handleSearch" inputmode="search" autocomplete="off" spellcheck="false" :maxlength="searchMaxLength" class="pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent w-full" id="dashboard-desktop-search-input">
              <Icon icon="tabler:search" class="absolute left-3 top-2.5 text-gray-400" width="20" />
            </div>
            <div class="relative hidden"></div>
          </div>
        </div>
        <div class="relative mt-4 md:hidden" id="dashboard-mobile-search-container">
          <input type="text" :placeholder="t('dashboard.searchAgentsPlaceholder')" :value="searchQuery" @input="handleSearch" inputmode="search" autocomplete="off" spellcheck="false" :maxlength="searchMaxLength" class="pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent w-full" id="dashboard-mobile-search-input">
          <Icon icon="tabler:search" class="absolute left-3 top-2.5 text-gray-400" width="20" />
        </div>
      </div>
    </div>

    <div v-if="isLoadingStats === true" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mb-4 grid-space-6 mt-2" id="dashboard-stats-cards-section-skeleton">
      <div v-for="n in 4" :key="'stats-skel-' + n" class="modern-panel p-5 dashboard-card">
        <div class="animate-pulse">
          <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
          <div class="h-7 bg-gray-200 dark:bg-gray-700 rounded w-20 mt-3"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-32 mt-3"></div>
        </div>
      </div>
    </div>
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mb-4 grid-space-6 mt-2" id="dashboard-stats-cards-section">
      <div class="modern-panel p-5 dashboard-card">
        <div class="flex items-start justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('dashboard.totalAgents') }}</p>
            <h2 class="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{{ formatNumber(stats.totalAgents > 0 ? stats.totalAgents : allAgents.length) }}</h2>
          </div>
          <div class="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/40 flex items-center justify-center"><Icon icon="hugeicons:ai-brain-03" width="20" class="text-blue-600 dark:text-blue-400"/></div>
        </div>
        <div class="mt-3 text-sm text-gray-500 dark:text-gray-400">{{ t('dashboard.overall') }}</div>
      </div>

      <div class="modern-panel p-5 dashboard-card">
        <div class="flex items-start justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('dashboard.recentConversations') }}</p>
            <h2 class="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{{ formatNumber(stats.recentConversations) }}</h2>
          </div>
          <div class="w-10 h-10 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center"><Icon icon="tabler:message-2" width="20" class="text-purple-600 dark:text-purple-400"/></div>
        </div>
        <div class="mt-3 text-sm text-gray-500 dark:text-gray-400">{{ t('dashboard.last30Days') }}</div>
      </div>

      <div class="modern-panel p-5 dashboard-card">
        <div class="flex items-start justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('dashboard.totalMessages') }}</p>
            <h2 class="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{{ formatNumber(stats.totalMessages) }}</h2>
          </div>
          <div class="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center"><Icon icon="tabler:message-dots" width="20" class="text-emerald-600 dark:text-emerald-400"/></div>
        </div>
        <div v-if="Number(stats.previousTotalMessages) < 1" class="mt-3 text-sm text-gray-500 dark:text-gray-400">{{ t('dashboard.last30Days') }}</div>
        <div class="mt-3 text-sm flex items-center" v-else-if="showTrend === true">
          <template v-if="isHebrew === true">
            <span class="text-gray-500 dark:text-gray-400 ml-2 text-right">{{ trendText }}</span>
            <span dir="ltr" class="inline-flex items-center px-2 py-0.5 rounded-full" :class="trendUp === true ? 'text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/30' : 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30'">{{ trendLabel }}</span>
          </template>
          <template v-else>
            <span dir="ltr" class="inline-flex items-center px-2 py-0.5 rounded-full mr-2" :class="trendUp === true ? 'text-green-700 bg-green-50 dark:text-green-300 dark:bg-green-900/30' : 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30'">{{ trendLabel }}</span>
            <span class="text-gray-500 dark:text-gray-400 text-left">{{ trendText }}</span>
          </template>
        </div>
        <div v-else class="mt-3 text-sm text-gray-500 dark:text-gray-400">{{ t('dashboard.last30Days') }}</div>
      </div>

      <div class="modern-panel p-5 dashboard-card">
        <div class="flex items-start justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">{{ t('dashboard.activeAgents') }}</p>
            <h2 class="text-3xl font-bold mt-1 text-gray-900 dark:text-white">{{ formatNumber(stats.activeAgents > 0 ? stats.activeAgents : activeAgentsCount) }}</h2>
          </div>
          <div class="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/40 flex items-center justify-center"><Icon icon="hugeicons:ai-brain-03" width="22" class="text-blue-600 dark:text-blue-400"/></div>
        </div>
        <div class="mt-3 text-sm text-gray-500 dark:text-gray-400">{{ t('dashboard.last30Days') }}</div>
      </div>
    </div>

   
    <div class="flex-1 grid grid-cols-1 lg:grid-cols-3 min-h-0 min-w-0" id="dashboard-main-content-area">
      <div class="lg:col-span-2 flex flex-col min-w-0" id="dashboard-agents-list-container">
        <div class="modern-panel p-4 h-full flex flex-col min-h-0 min-w-0 dashboard-card" id="dashboard-agents-list-card">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4" id="dashboard-agents-list-header">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white" id="dashboard-agents-list-title">{{ t('dashboard.availableAIAgents') }}</h2>
            <div class="flex items-center mt-3 sm:mt-0" :class="isHebrew === true ? 'space-x-reverse space-x-2' : 'space-x-2'" id="dashboard-view-toggle-buttons">
              <button @click="toggleView('grid')" :class="viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'" class="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" id="dashboard-view-grid-button"><Icon icon="tabler:layout-grid" width="20" /></button>
              <button @click="toggleView('list')" :class="viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'" class="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" id="dashboard-view-list-button"><Icon icon="tabler:layout-list" width="20" /></button>
              <div class="min-w-0 w-36 sm:w-44 md:w-48" id="dashboard-agent-type-filters">
                <MultiSelect 
                  v-model="agentVisibilitySelected" 
                  :options="agentVisibilityOptions" 
                  :maxSelections="1"
                  :placeholder="t('dashboard.overall')"
                  :label="''"
                  buttonClass="btn-sm h-9"
                />
              </div>
            </div>
          </div>

          <div v-if="isLoadingAgents === true" class="grid grid-cols-1 md:grid-cols-2 grid-space-4" id="dashboard-agents-loading-grid">
            <div v-for="n in itemsPerPage" :key="n" class="rounded-lg border border-gray-200 dark:border-gray-700 p-4 animate-pulse bg-gray-50 dark:bg-gray-700 h-28"></div>
          </div>

          <div v-else class="flex-1" id="dashboard-agents-scrollable-list">
            <template v-if="useModernDashboard !== true">
              <div v-if="viewMode === 'grid'" id="dashboard-agents-grid-view" class="grid-space-4">
                <div v-for="agent in paginatedAgents" :key="agent.agentid" class="agent-card cursor-pointer" @click="navigateToAgent(agent)">
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <div class="w-12 h-12 rounded-lg flex items-center justify-center" :class="getAgentIcon(agent) !== '' && agent.agenttype !== 'HelpAgent' ? 'bg-white' : (agent.agenttype === 'HelpAgent' ? 'bg-yellow-100 dark:bg-yellow-900' : 'bg-blue-100 dark:bg-blue-900')">
                        <img v-if="getAgentIcon(agent) !== '' && agent.agenttype !== 'HelpAgent'" :src="getAgentIcon(agent)" class="w-10 h-10 object-contain rounded" alt="" />
                        <Icon v-else :icon="agent.agenttype === 'HelpAgent' ? 'tabler:help-circle' : 'mingcute:ai-line'" width="24" :class="agent.agenttype === 'HelpAgent' ? 'text-yellow-500 dark:text-yellow-400' : 'text-blue-500 dark:text-blue-400'" />
                      </div>
                    </div>
                    <div class="ml-4 mr-4 flex-1">
                      <h3 class="text-lg font-semibold text-gray-900 dark:text-white transition-colors" :class="isHebrew === true ? 'text-right' : 'text-left'">{{ agent.agentname }}</h3>
                      <p class="text-gray-600 dark:text-gray-400 mt-1 text-sm" :title="agent.agentShortDescription" :class="isHebrew === true ? 'text-right' : 'text-left'">{{ agent.agentShortDescription !== undefined && agent.agentShortDescription !== null && agent.agentShortDescription.length > 0 ? agent.agentShortDescription : t('dashboard.noDescription') }}</p>
                      <div class="mt-2 flex items-center justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">{{ agent.agenttype === 'HelpAgent' ? t('dashboard.alwaysAvailable') : t('dashboard.lastActive') + ' ' + formatDate(agent.updatedat) }}</span>
                        <button @click.stop="handlePrimaryAgentAction(agent)" class="inline-flex items-center px-3 py-2 text-xs font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500" :class="isHebrew === true ? 'ml-2' : 'mr-2'">{{ resolvePrimaryAgentButtonLabel(agent) }}<Icon :icon="agent.agenttype === 'HelpAgent' ? 'tabler:help-circle' : 'tabler:arrow-right'" width="18" :class="isHebrew === true ? 'mr-2' : 'ml-2'" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <template v-if="useModernDashboard === true">
              <ModernAgentGrid 
                v-if="viewMode === 'grid'"
                :agents="paginatedAgents"
                :is-hebrew="isHebrew"
                :style-logos="styleLogos"
                :show-model-label="false"
                :get-open-button-label="resolvePrimaryAgentButtonLabel"
                @openChat="handlePrimaryAgentAction"
                @navigate="navigateToAgent"
              />
            </template>

            <div v-if="viewMode !== 'grid'" class="space-y-4">
              <div v-for="agent in paginatedAgents" :key="agent.agentid" class="agent-card cursor-pointer" @click="navigateToAgent(agent)">
                <div class="flex items-center h-full">
                  <div class="flex-shrink-0">
                    <div class="w-10 h-10 rounded-lg flex items-center justify-center" :class="getAgentIcon(agent) !== '' && agent.agenttype !== 'HelpAgent' ? 'bg-white' : (agent.agenttype === 'HelpAgent' ? 'bg-yellow-100 dark:bg-yellow-900' : 'bg-blue-100 dark:bg-blue-900')">
                      <img v-if="getAgentIcon(agent) !== '' && agent.agenttype !== 'HelpAgent'" :src="getAgentIcon(agent)" class="w-8 h-8 object-contain rounded" alt="" />
                      <Icon v-else :icon="agent.agenttype === 'HelpAgent' ? 'tabler:help-circle' : 'mingcute:ai-line'" width="20" :class="agent.agenttype === 'HelpAgent' ? 'text-yellow-500 dark:text-yellow-400' : 'text-blue-500 dark:text-blue-400'" />
                    </div>
                  </div>
                  <div class="ml-4 mr-4 flex-1">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white transition-colors" :class="isHebrew === true ? 'text-right' : 'text-left'">{{ agent.agentname }}</h3>
                    <p class="text-gray-600 dark:text-gray-400 text-sm" :title="agent.agentShortDescription" :class="isHebrew === true ? 'text-right' : 'text-left'">{{ agent.agentShortDescription !== undefined && agent.agentShortDescription !== null && agent.agentShortDescription.length > 0 ? agent.agentShortDescription : (agent.agenttype === 'HelpAgent' ? t('dashboard.alwaysAvailable') : t('dashboard.noDescription')) }}</p>
                  </div>
                  <div class="flex items-center flex-col items-start sm:flex-row sm:items-center" :class="isHebrew === true ? 'space-x-reverse space-x-4' : 'space-x-4'">
                    <span class="text-xs text-gray-500 dark:text-gray-400">{{ agent.agenttype === 'HelpAgent' ? t('dashboard.alwaysAvailable') : t('dashboard.lastActive') + ' ' + formatDate(agent.updatedat) }}</span>
                    <button @click.stop="handlePrimaryAgentAction(agent)" class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500" :class="isHebrew === true ? 'ml-2' : 'mr-2'">{{ resolvePrimaryAgentButtonLabel(agent) }}<Icon :icon="agent.agenttype === 'HelpAgent' ? 'tabler:help-circle' : 'tabler:arrow-right'" width="20" :class="isHebrew === true ? 'mr-2' : 'ml-2'" /></button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="visibleFilteredAgents.length === 0" class="h-full min-h-[360px] flex flex-col items-center justify-center text-center px-6" id="dashboard-no-agents-found-message">
              <div class="w-16 h-16 rounded-2xl border border-blue-200 dark:border-blue-800/70 bg-gradient-to-br from-blue-50 to-sky-100 dark:from-blue-900/50 dark:to-slate-900 flex items-center justify-center shadow-sm">
                <Icon icon="mingcute:ai-fill" width="30" class="text-blue-600 dark:text-blue-300" />
              </div>
              <h3 class="mt-5 text-xl font-semibold text-gray-900 dark:text-gray-100" :class="isHebrew === true ? 'text-right' : 'text-left'">{{ t('dashboard.noAgentsYet') }}</h3>
              <p class="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md" :class="isHebrew === true ? 'text-right' : 'text-left'">{{ t('dashboard.noAgentsSubtitle') }}</p>
              <button @click="router.push('/aiChatAgents')" dir="ltr" class="mt-5 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500">
                <Icon icon="tabler:plus" width="18" class="mr-2" />
                {{ t('dashboard.newAgent') }}
              </button>
            </div>
          </div>
          

          <div v-if="totalPages > 1" class="mt-2 flex justify-center items-center" :class="isHebrew === true ? 'space-x-reverse space-x-1 sm:space-x-2' : 'space-x-1 sm:space-x-2'" id="dashboard-agents-pagination-controls">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              :class="isHebrew === true ? 'mr-2' : 'ml-2'"
              id="dashboard-pagination-prev-button"
            >
              {{ t('dashboard.previous') }}
            </button>
            <template v-for="pageNumber in paginationRange" :key="pageNumber">
                <button
                  v-if="typeof pageNumber === 'number'"
                  @click="goToPage(pageNumber)"
                  :class="{
                    'bg-amber-600 text-white border-amber-600': currentPage === pageNumber,
                    'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700': currentPage !== pageNumber
                  }"
                  class="px-2 py-1 text-xs font-medium border rounded-md"
                  :id="`dashboard-pagination-page-${pageNumber}`"
                >
                  {{ pageNumber }}
                </button>
                <span v-else class="px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400" :id="`dashboard-pagination-dots-${pageNumber}`">{{ pageNumber }}</span>
            </template>
            <button
              @click="nextPage"
              :disabled="currentPage === totalPages"
              class="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              id="dashboard-pagination-next-button"
            >
              {{ t('dashboard.next') }}
            </button>
          </div>

        </div>
      </div>

      <div class="min-h-0 min-w-0" id="dashboard-sidebar-actions-and-activity">
        <template v-if="useModernDashboard !== true">
          <div class="modern-panel p-4 dashboard-card" id="dashboard-performance-section">
            <div class="flex items-center justify-between mb-4"><h2 class="text-xl font-semibold text-gray-900 dark:text-white">{{ t('dashboard.performance') }}</h2><span class="text-sm text-gray-500 dark:text-gray-400">{{ timeRangeLabel }}</span></div>
            <div v-if="isLoadingStats === true" class="flex flex-wrap">
              <div class="flex items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex-1 m-3 animate-pulse">
                <div class="w-9 h-9 rounded-md bg-gray-200 dark:bg-gray-600" :class="isHebrew === true ? 'ml-4' : 'mr-4'"></div>
                <div class="flex-1">
                  <div class="h-3 bg-gray-200 dark:bg-gray-600 rounded w-16"></div>
                  <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24 mt-2"></div>
                </div>
              </div>
              <div class="flex items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex-1 m-3 animate-pulse">
                <div class="w-9 h-9 rounded-md bg-gray-200 dark:bg-gray-600" :class="isHebrew === true ? 'ml-4' : 'mr-4'"></div>
                <div class="flex-1">
                  <div class="h-3 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
                  <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24 mt-2"></div>
                </div>
              </div>
              <div class="flex items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex-1 m-3 animate-pulse">
                <div class="w-9 h-9 rounded-md bg-gray-200 dark:bg-gray-600" :class="isHebrew === true ? 'ml-4' : 'mr-4'"></div>
                <div class="flex-1">
                  <div class="h-3 bg-gray-200 dark:bg-gray-600 rounded w-20"></div>
                  <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24 mt-2"></div>
                </div>
              </div>
            </div>
            <div v-else class="flex flex-wrap">
              <div class="flex items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex-1 m-3">
                <div class="w-9 h-9 rounded-md bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center" :class="isHebrew === true ? 'ml-4' : 'mr-4'"><Icon icon="tabler:activity" width="18" class="text-blue-600 dark:text-blue-400"/></div>
                <div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">{{ t('dashboard.throughput') }}</div>
                  <div class="text-base font-semibold text-gray-900 dark:text-white">{{ formatNumber(stats.totalMessages) }}</div>
                </div>
              </div>
              <div class="flex items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex-1 m-3">
                <div class="w-9 h-9 rounded-md bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center" :class="isHebrew === true ? 'ml-4' : 'mr-4'"><Icon icon="tabler:lock" width="18" class="text-indigo-600 dark:text-indigo-400"/></div>
                <div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">{{ t('dashboard.privateAgents') }}</div>
                  <div class="text-base font-semibold text-gray-900 dark:text-white">{{ formatNumber(stats.privateAgents != undefined && stats.privateAgents > 0 ? stats.privateAgents : 0) }}</div>
                </div>
              </div>
              <div class="flex items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-700 flex-1 m-3">
                <div class="w-9 h-9 rounded-md bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center" :class="isHebrew === true ? 'ml-4' : 'mr-4'"><Icon icon="tabler:world" width="18" class="text-teal-600 dark:text-teal-400"/></div>
                <div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">{{ t('dashboard.publicAgents') }}</div>
                  <div class="text-base font-semibold text-gray-900 dark:text-white">{{ formatNumber(stats.publicAgents != undefined && stats.publicAgents > 0 ? stats.publicAgents : 0) }}</div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-if="useModernDashboard === true">
          <div class="modern-panel p-4 dashboard-card" id="dashboard-usage-section">
            <ModernUsageGrid />
          </div>
        </template>
        <div v-if="isAgentEditor !== true" class="modern-panel p-4 dashboard-card" id="dashboard-quick-actions-panel">
          <DashboardQuickActions />
        </div>
        <div v-if="false === true" class="bg-white dark:bg-gray-800 rounded-2xl ring-1 ring-gray-100 dark:ring-gray-700 p-6 flex-1 overflow-hidden dashboard-card" id="dashboard-recent-activity-section"></div>
      </div>
    </div>
    <OpenAgentChannelsModal
      :visible="showOpenAgentModal === true"
      :isRtl="currentLanguage === 'he' || currentLanguage === 'ar'"
      :channels="openAgentModalChannels"
      @close="closeOpenAgentModal"
      @open="openSelectedAgentChannel"
    />
    <component :is="'style'" id="organization-custom-styles-from-idb-dashboard">{{ orgCss }}</component>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch, reactive, nextTick } from 'vue';
import { Icon } from "@iconify/vue/dist/offline";
import { useToast } from 'vue-toastification';
import { globalState } from '@/globalState';
import { useRouter } from 'vue-router';
import { useI18n } from '@/i18n.js';
import { debounce } from 'lodash-es';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import { numberToHebrewLetters, getHebrewMonthName } from '@/utils/hebrewNumerals.js';
import MultiSelect from '@/components/MultiSelect.vue';
import DashboardQuickActions from '@/components/DashboardQuickActions.vue';
import ModernAgentGrid from '@/components/ModernAgentGrid.vue';
import ModernUsageGrid from '@/components/ModernUsageGrid.vue';
import OpenAgentChannelsModal from '@/shared/components/OpenAgentChannelsModal.vue';
import { getAllAIChats } from '@/shared/services/aiChatsService';
import { initDB, loadOrgStyles } from '@/indexedDBService';
import { getActiveThemeKey, normalizeThemeKey } from '@/Themes/themeRuntime.js';
import { buildOpenAgentChannels, getOpenAgentButtonLabel, normalizeOpenAgentPhoneDigits, normalizeOpenAgentSelectedChannels, normalizeOpenAgentTelValue, shouldOpenSingleAgentChannelDirectly } from '@/shared/utils/openAgentChannels.js';

function resolveAppliedThemeKey() {
  if (typeof document !== 'undefined') {
    const linkEl = document.getElementById('platform-theme-styles');
    const href = linkEl !== null && linkEl !== undefined && typeof linkEl.href === 'string' ? linkEl.href : '';
    const normalizedHref = typeof href === 'string' ? href.trim() : '';
    if (normalizedHref.length > 0) {
      return normalizeThemeKey(normalizedHref);
    }
  }
  return getActiveThemeKey();
}

const appliedThemeKey = computed(() => resolveAppliedThemeKey());
const isModernTheme = computed(() => appliedThemeKey.value === 'modern');
const isLightTheme = computed(() => appliedThemeKey.value === 'light');
const useModernDashboard = computed(() => isModernTheme.value === true || isLightTheme.value === true);
const dashboardContainerClass = computed(() => {
  if (useModernDashboard.value === true) {
    if (isLightTheme.value === true) {
      return 'dashboard-modern dashboard-light px-4 pt-4 pb-0 mb-0 w-full max-w-full overflow-x-hidden';
    }
    return 'dashboard-modern bg-white dark:bg-gray-900 px-4 pt-4 pb-0 mb-0 w-full max-w-full overflow-x-hidden';
  }
  return 'dashboard mb-4 p-8 bg-white-50 w-full max-w-full overflow-x-hidden';
});

const router = useRouter();
const toast = useToast();
const { t, currentLanguage } = useI18n();

const orgCss = ref('');

const searchMaxLength = 120;
const chatMaxLength = 800;

const userName = computed(() => isDefinedAndNotEmpty(globalState.userInfo?.userName) != false ? globalState.userInfo?.userName : 'Admin');
const currentDateTime = ref('');
const currentHebrewDate = ref('');
const isHebrew = computed(() => currentLanguage.value === 'he');
const isAgentEditor = computed(() => {
  const userInfo = globalState.userInfo;
  const roles = userInfo !== null && userInfo !== undefined && Array.isArray(userInfo.roles) === true ? userInfo.roles : [];
  return roles.some(r => typeof r === 'string' && r.trim().toLowerCase() === 'agent editor') === true;
});
const availableAgents = ref([]);
const allAgents = ref([]);
const agentVisibilityFilter = ref('all');
const styleLogos = ref({});
const agentVisibilitySelected = ref(['all']);
const agentVisibilityOptions = [
  { label: 'All agents', value: 'all' },
  { label: 'Private agents', value: 'private' },
  { label: 'Public agents', value: 'public' }
];
const stats = ref({
  totalAgents: 0,
  recentConversations: 0,
  totalMessages: 0,
  previousTotalMessages: 0,
  activeAgents: 0,
  privateAgents: 0,
  publicAgents: 0
});
const viewMode = ref('grid');
const searchQuery = ref('');
const chatInput = ref('');
const recentActivities = ref([]);
const isLoadingAgents = ref(true);
const isLoadingStats = ref(true);
const timeRange = ref('30d');
const showRangeMenu = ref(false);
const timeRanges = [
  { label: t('dashboard.last7Days') !== undefined && t('dashboard.last7Days') !== null ? t('dashboard.last7Days') : 'Last 7 days', value: '7d' },
  { label: t('dashboard.last30Days') !== undefined && t('dashboard.last30Days') !== null ? t('dashboard.last30Days') : 'Last 30 days', value: '30d' },
  { label: t('dashboard.last90Days') !== undefined && t('dashboard.last90Days') !== null ? t('dashboard.last90Days') : 'Last 90 days', value: '90d' },
  { label: t('dashboard.lastYear') !== undefined && t('dashboard.lastYear') !== null ? t('dashboard.lastYear') : 'Last year', value: '1y' }
];

const currentPage = ref(1);
const itemsPerPage = ref(2);
let dashboardResizeObserver = null;

const helpAgent = {
  agentid: 'system-help-agent',
  agentname: t('aiChatAgentSetup.helpAgent.title'),
  agentdescription: `${t('aiChatAgentSetup.helpAgent.greeting', { appName: isDefinedAndNotEmpty(globalState.whiteLabelName) != false ? globalState.whiteLabelName : 'AIChats' })}`,
  agenttype: 'HelpAgent', 
  updatedat: null, 
};

const showOpenAgentModal = ref(false);
const openAgentModalChannels = ref([]);
const currentOpenAgent = ref(null);
const openAgentBindingsByAgentId = reactive({});

const allFilteredAgents = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  let baseAgents = [];
  if (availableAgents.value !== undefined && availableAgents.value !== null) {
    baseAgents = availableAgents.value;
  }
  let filteredAgents = [];
  if (baseAgents !== undefined && baseAgents !== null) {
    filteredAgents = baseAgents.filter(agent => 
      agent.agentname.toLowerCase().includes(query) !== false ||
      (agent.agentShortDescription !== undefined && agent.agentShortDescription !== null && agent.agentShortDescription.toLowerCase().includes(query) !== false) ||
      (agent.agentdescription !== undefined && agent.agentdescription !== null && agent.agentdescription.toLowerCase().includes(query) !== false)
    );
  }
  
  let helpAgentMatchesQuery = false;
  if (helpAgent.agentname !== undefined && helpAgent.agentname !== null) {
    if (helpAgent.agentname.toLowerCase().includes(query) !== false) {
        helpAgentMatchesQuery = true;
    }
  }
  if (helpAgentMatchesQuery === false) {
    if (helpAgent.agentdescription !== undefined && helpAgent.agentdescription !== null) {
        if (helpAgent.agentdescription.toLowerCase().includes(query) === true) {
            helpAgentMatchesQuery = true;
        }
    }
  }

  if (query.length === 0) {
    if (agentVisibilityFilter.value === 'public') {
      return [...baseAgents];
    }
    return [...baseAgents, helpAgent];
  } else {
    let result = [...filteredAgents];
    if (helpAgentMatchesQuery === true && agentVisibilityFilter.value !== 'public') {
      let helpAgentInResults = false;
      if (result !== undefined && result !== null) {
        if (result.some(a => a.agentid === helpAgent.agentid) === true) {
            helpAgentInResults = true;
        }
      }
      if (helpAgentInResults === false) {
        result.push(helpAgent);
      }
    }
    return result;
  }
});

const visibleFilteredAgents = computed(() => {
  if (Array.isArray(allFilteredAgents.value) !== true) {
    return [];
  }
  return allFilteredAgents.value.filter(agent => agent?.agenttype !== 'HelpAgent');
});

const totalPages = computed(() => {
  if (visibleFilteredAgents.value !== undefined && visibleFilteredAgents.value !== null) {
    if (visibleFilteredAgents.value.length === 0) {
      return 1;
    }
  return Math.ceil(visibleFilteredAgents.value.length / itemsPerPage.value);
  }
  return 1;
});

const paginatedAgents = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  if (visibleFilteredAgents.value !== undefined && visibleFilteredAgents.value !== null) {
  return visibleFilteredAgents.value.slice(start, end);
  }
  return [];
});

watch(searchQuery, () => {
  currentPage.value = 1;
});

watch(itemsPerPage, async () => {
  if (currentPage.value > totalPages.value) {
    currentPage.value = totalPages.value;
  }
  await nextTick();
  updateGridCapacity();
});

watch(agentVisibilitySelected, () => {
  if (Array.isArray(agentVisibilitySelected.value) === true && agentVisibilitySelected.value.length > 0) {
    agentVisibilityFilter.value = agentVisibilitySelected.value[0];
  } else {
    agentVisibilityFilter.value = 'all';
  }
  updateAvailableAgentsForVisibility();
  currentPage.value = 1;
});

const paginationRange = computed(() => {
  const current = currentPage.value;
  const last = totalPages.value;
  const delta = 1; 
  const left = current - delta;
  const right = current + delta + 1;
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= last; i = i + 1) {
    if (i === 1 || i === last || (i >= left && i < right)) {
      range.push(i);
    }
  }

  for (let i of range) {
    if (l !== undefined) {
      if ((i - l) === 2) {
        rangeWithDots.push(l + 1);
      } else if ((i - l) !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }
  return rangeWithDots;
});

function nextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value = currentPage.value + 1;
  }
}

function prevPage() {
  if (currentPage.value > 1) {
    currentPage.value = currentPage.value - 1;
  }
}

function goToPage(pageNumber) {
  if (pageNumber >= 1 && pageNumber <= totalPages.value) {
    currentPage.value = pageNumber;
  }
}

function updateGridCapacity() {
  if (typeof document === 'undefined' || isLoadingAgents.value === true) {
    return;
  }
  const container = document.getElementById('dashboard-agents-scrollable-list');
  if (container === null || container.clientHeight < 1) {
    return;
  }
  const cardSelector = viewMode.value === 'grid' ? '.modern-agent-card, .agent-card' : '.agent-card';
  const cards = container.querySelectorAll(cardSelector);
  if (cards.length < 1) {
    return;
  }
  const firstCard = cards[0];
  const cardHeight = firstCard.getBoundingClientRect().height;
  if (cardHeight < 1) {
    return;
  }
  const cardStyle = window.getComputedStyle(firstCard);
  const parsedMarginTop = Number.parseFloat(cardStyle.marginTop);
  const marginTop = Number.isFinite(parsedMarginTop) === true ? parsedMarginTop : 0;
  const parsedMarginBottom = Number.parseFloat(cardStyle.marginBottom);
  const marginBottom = Number.isFinite(parsedMarginBottom) === true ? parsedMarginBottom : 0;
  let columns = 1;
  if (viewMode.value === 'grid') {
    if (container.clientWidth >= 1000) {
      columns = 3;
    } else if (container.clientWidth >= 520) {
      columns = 2;
    }
  }
  const rowHeight = cardHeight + marginTop + marginBottom;
  const usableHeight = Math.max(container.clientHeight - 8, cardHeight);
  const rows = Math.max(1, Math.floor((usableHeight + marginTop + marginBottom) / rowHeight));
  const nextCapacity = Math.max(columns, rows * columns);
  if (itemsPerPage.value !== nextCapacity) {
    itemsPerPage.value = nextCapacity;
  }
}

function updateDateTime() {
  const now = new Date();
  let locale = 'en-US';
  if (currentLanguage.value === 'he') {
    locale = 'he-IL';
  }
  currentDateTime.value = new Intl.DateTimeFormat(locale, {
    dateStyle: 'full',
    timeStyle: 'short',
    timeZone: 'Asia/Jerusalem'
  }).format(now);
  if (currentLanguage.value === 'he') {
    currentDateTime.value = '\u2067' + currentDateTime.value + '\u2069';
  }
  let hebrew = '';
  try {
    hebrew = new Intl.DateTimeFormat('he-IL-u-ca-hebrew', { dateStyle: 'full', timeZone: 'Asia/Jerusalem' }).format(now);
  } catch (e) {
    hebrew = '';
  }
  if (hebrew === '') {
    try {
      hebrew = now.toLocaleDateString('he-IL', { dateStyle: 'full', calendar: 'hebrew', timeZone: 'Asia/Jerusalem' });
    } catch (e2) {
      hebrew = '';
    }
  }
  if (hebrew === '') {
    try {
      hebrew = new Intl.DateTimeFormat('he-u-ca-hebrew', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Asia/Jerusalem' }).format(now);
    } catch (e3) {
      hebrew = '';
    }
  }
  if (hebrew !== '') {
    let parts = [];
    try {
      parts = new Intl.DateTimeFormat('he-IL-u-ca-hebrew-nu-latn', { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'Asia/Jerusalem' }).formatToParts(now);
    } catch (e4) {
      try {
        parts = new Intl.DateTimeFormat('he-IL-u-ca-hebrew', { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: 'Asia/Jerusalem' }).formatToParts(now);
      } catch (e5) {
        parts = [];
      }
    }
    if (Array.isArray(parts) === true && parts.length > 0) {
      let dayNum = '';
      let monthNum = '';
      let yearNum = '';
      for (const p of parts) {
        if (p.type === 'day') { dayNum = p.value; }
        if (p.type === 'month') { monthNum = p.value; }
        if (p.type === 'year') { yearNum = p.value; }
      }
      if (dayNum !== '' && monthNum !== '' && yearNum !== '') {
        const dayText = numberToHebrewLetters(dayNum);
        const monthText = getHebrewMonthName(Number(monthNum), Number(yearNum));
        const yearText = numberToHebrewLetters(yearNum);
        if (dayText !== '' && monthText !== '' && yearText !== '') {
          hebrew = `${dayText} ${monthText} ${yearText}`;
        }
      }
    }
  }
  currentHebrewDate.value = hebrew.length > 0 ? ('\u2067' + hebrew + '\u2069') : '';
}

async function loadAvailableAgents() {
  try {
    isLoadingAgents.value = true;
    const data = await getAllAIChats({ includeStyles: true });
    if (Array.isArray(data) === true) {
      allAgents.value = data;
      updateAvailableAgentsForVisibility();
      const nextLogos = {};
      for (const a of data) {
        const guid = normalizeStyleGuid(a);
        const agentId = a !== undefined && a !== null && a.agentid !== undefined && a.agentid !== null ? String(a.agentid) : '';
        const style = a !== undefined && a !== null ? (a.agentStyle !== undefined && a.agentStyle !== null ? a.agentStyle : (a.agentstyle !== undefined && a.agentstyle !== null ? a.agentstyle : undefined)) : undefined;
        const logoBase64 = style !== undefined && style !== null && typeof style.logoBase64 === 'string' ? style.logoBase64 : (style !== undefined && style !== null && typeof style.logobase64 === 'string' ? style.logobase64 : '');
        if (logoBase64 !== '') {
          const url = logoBase64.startsWith('data:image') === true ? logoBase64 : `data:image/png;base64,${logoBase64}`;
          if (agentId !== '') {
            nextLogos[agentId] = url;
          }
          if (guid !== '') {
            nextLogos[guid] = url;
          }
        }
      }
      styleLogos.value = nextLogos;
    } else {
      toast.error('Invalid data format received from server');
      allAgents.value = [];
      availableAgents.value = [];
      styleLogos.value = {};
    }
  } catch (error) {
    console.error('Error loading agents:', error);
    const errorText = typeof error?.message === 'string' ? error.message : '';
    if (errorText !== '') {
      const lowered = errorText.toLowerCase();
      if (lowered.includes('jwt expired') === true) {
        isLoadingAgents.value = false;
        router.push('/login');
        return;
      }
    }
    allAgents.value = [];
    availableAgents.value = []; 
    const msg = typeof error?.response?.data?.reason === 'string' ? error.response.data.reason : (errorText !== '' ? errorText : 'Unknown error');
    toast.error('Error loading agents: ' + msg);
  }
  isLoadingAgents.value = false;
  await nextTick();
  updateGridCapacity();
}

async function loadDashboardStats() {
  try {
    isLoadingStats.value = true;
    const response = await fetch(`${globalState.apiEndpoint}/dashboard/stats?range=${timeRange.value}`, {
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (shouldRedirectForStatus(response.status) === true) {
      isLoadingStats.value = false;
      return;
    }
    if (response.ok === true) {
      const data = await response.json();
      if (shouldRedirectForMessage(data) === true) {
        isLoadingStats.value = false;
        return;
      }
      stats.value = {
        totalAgents: isDefinedAndNotEmpty(data.totalAgents) != false ? data.totalAgents : 0,
        recentConversations: isDefinedAndNotEmpty(data.recentConversations) != false ? data.recentConversations : 0,
        totalMessages: isDefinedAndNotEmpty(data.totalMessages) != false ? data.totalMessages : 0,
        previousTotalMessages: isDefinedAndNotEmpty(data.previousTotalMessages) != false ? data.previousTotalMessages : 0,
        activeAgents: isDefinedAndNotEmpty(data.activeAgents) != false ? data.activeAgents : 0,
        privateAgents: isDefinedAndNotEmpty(data.privateAgents) != false ? data.privateAgents : 0,
        publicAgents: isDefinedAndNotEmpty(data.publicAgents) != false ? data.publicAgents : 0
      };
      recentActivities.value = [];
    } else {
      const error = await response.json();
      if (shouldRedirectForMessage(error) === true) {
        isLoadingStats.value = false;
        return;
      }
      toast.error(isDefinedAndNotEmpty(error.message) != false ? error.message : 'Failed to load dashboard data');
      stats.value = { totalAgents: 0, recentConversations: 0, totalMessages: 0, previousTotalMessages: 0, activeAgents: 0, privateAgents: 0, publicAgents: 0 };
      recentActivities.value = [];
    }
  } catch (error) {
    console.error('Error loading dashboard data:', error);
    const errorText = typeof error?.message === 'string' ? error.message : '';
    if (errorText !== '') {
      const lowered = errorText.toLowerCase();
      if (lowered.includes('jwt expired') === true) {
        isLoadingStats.value = false;
        router.push('/login');
        return;
      }
    }
    const fallbackMessage = errorText !== '' ? errorText : 'Unknown error';
    toast.error('Error loading dashboard data: ' + fallbackMessage);
    stats.value = { totalAgents: 0, recentConversations: 0, totalMessages: 0, previousTotalMessages: 0, activeAgents: 0, privateAgents: 0, publicAgents: 0 };
    recentActivities.value = [];
  }
  buildSparklines();
  isLoadingStats.value = false;
}

function getAuthHeaders() {
  const authTokenCookie = document.cookie.split('; ').find(row => row.startsWith('authToken=') !== false);
  const jwtTokenCookie = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') !== false);
  let token = undefined;
  if (authTokenCookie !== undefined && authTokenCookie !== null) {
    token = authTokenCookie.split('=')[1];
  } else if (jwtTokenCookie !== undefined && jwtTokenCookie !== null) {
    token = jwtTokenCookie.split('=')[1];
  }
  
  if (token === undefined || token === null || token === '') {
    return {};
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

function shouldRedirectForStatus(status) {
  if (status === 401 || status === 403) {
    router.push('/login');
    return true;
  }
  return false;
}

function extractJwtMessage(payload) {
  if (typeof payload?.message === 'string') {
    return payload.message;
  }
  if (typeof payload?.error === 'string') {
    return payload.error;
  }
  return '';
}

function shouldRedirectForMessage(payload) {
  const tokenMessage = extractJwtMessage(payload);
  if (tokenMessage !== '') {
    const lowered = tokenMessage.toLowerCase();
    if (lowered.includes('jwt expired') === true) {
      router.push('/login');
      return true;
    }
  }
  return false;
}

function navigateToAgent(agent) {
  if (isAgentEditor.value === true) {
    if (agent.agentid === helpAgent.agentid) {
      router.push('/aiChatAgents');
      return;
    }
    const safeAgentId = encodeURIComponent(String(agent.agentid));
    router.push(`/aiChatAgentSetup?edit=${safeAgentId}`);
    return;
  }
  if (agent.agentid === helpAgent.agentid) {
    router.push('/helpAgent');
  } else {
    if (agent.agenttype === 'PublicAgent') {
      const agentIdRaw = agent.agentid;
      if (agentIdRaw === undefined || agentIdRaw === null) {
        return;
      }
      const safeAgentId = encodeURIComponent(String(agentIdRaw));
      const url = `${window.location.origin}/agent/${safeAgentId}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      router.push({
        name: 'ChatAgentAssistance',
        params: { agentid: agent.agentid }
      });
    }
  }
}

function getConfiguredDeploymentChannels(agent) {
  if (agent !== null && agent !== undefined && Array.isArray(agent.availableDeploymentChannels) === true) {
    return normalizeOpenAgentSelectedChannels(agent.availableDeploymentChannels);
  }
  const configured = agent !== null && agent !== undefined && agent.agentdata !== undefined && agent.agentdata !== null && agent.agentdata.configurations !== undefined && agent.agentdata.configurations !== null
    ? agent.agentdata.configurations.deploymentChannels
    : [];
  return normalizeOpenAgentSelectedChannels(configured);
}

function getAvailableOpenAgentChannelsForAgent(agent) {
  const selectedChannels = getConfiguredDeploymentChannels(agent);
  const cachedBindings = getCachedOpenAgentBindings(agent);
  return buildOpenAgentChannels({
    selectedChannels,
    whatsappNumber: cachedBindings.whatsappDisplayNumber,
    phoneNumber: cachedBindings.phoneDisplayNumber,
    t
  });
}

function resolvePrimaryAgentButtonLabel(agent) {
  if (agent === null || agent === undefined) {
    return t('aiChatAgentSetup.buttons.openAgent', 'Open Agent');
  }
  if (agent.agentid === helpAgent.agentid) {
    return t('dashboard.getHelp');
  }
  if (isAgentEditor.value === true) {
    return t('dashboard.startChat');
  }
  const availableChannels = getAvailableOpenAgentChannelsForAgent(agent);
  if (availableChannels.length > 0) {
    return getOpenAgentButtonLabel(availableChannels, t);
  }
  const configuredChannels = getConfiguredDeploymentChannels(agent);
  if (configuredChannels.length === 1 && configuredChannels[0] === 'Web') {
    return t('aiChatAgentSetup.buttons.openChat', 'Open Chat');
  }
  return t('aiChatAgentSetup.buttons.openAgent', 'Open Agent');
}

async function handlePrimaryAgentAction(agent) {
  if (agent === null || agent === undefined) {
    return;
  }
  if (agent.agentid === helpAgent.agentid || isAgentEditor.value === true) {
    navigateToAgent(agent);
    return;
  }
  await openAgentForRecord(agent);
}

function getOpenAgentBindingsCacheKey(agent) {
  if (agent !== null && agent !== undefined && agent.agentid !== undefined && agent.agentid !== null) {
    return String(agent.agentid);
  }
  return '';
}

function extractBindingDisplayNumber(data) {
  if (data !== null && data !== undefined && typeof data.number === 'string' && data.number.trim() !== '') {
    return data.number.trim();
  }
  if (data !== null && data !== undefined && typeof data.externalNumber === 'string' && data.externalNumber.trim() !== '') {
    return data.externalNumber.trim();
  }
  if (data !== null && data !== undefined && typeof data.display === 'string' && data.display.trim() !== '') {
    return data.display.trim();
  }
  return '';
}

function getCachedOpenAgentBindings(agent) {
  const cacheKey = getOpenAgentBindingsCacheKey(agent);
  if (cacheKey === '') {
    return { whatsappDisplayNumber: '', phoneDisplayNumber: '' };
  }
  if (openAgentBindingsByAgentId[cacheKey] !== undefined) {
    return openAgentBindingsByAgentId[cacheKey];
  }
  return { whatsappDisplayNumber: '', phoneDisplayNumber: '' };
}

async function fetchOpenAgentBindingDisplayNumber(path) {
  try {
    const response = await fetch(`${globalState.apiEndpoint}${path}`, {
      headers: getAuthHeaders(),
      credentials: 'include'
    });
    if (shouldRedirectForStatus(response.status) === true) {
      return '';
    }
    if (response.ok !== true) {
      return '';
    }
    const data = await response.json();
    if (shouldRedirectForMessage(data) === true) {
      return '';
    }
    return extractBindingDisplayNumber(data);
  } catch (error) {
    const errorText = typeof error?.message === 'string' ? error.message : '';
    if (errorText !== '') {
      const lowered = errorText.toLowerCase();
      if (lowered.includes('jwt expired') === true) {
        router.push('/login');
      }
    }
    return '';
  }
}

function openWebAgentChannelForAgent(agent) {
  closeOpenAgentModal();
  navigateToAgent(agent);
}

function openPhoneAgentNumber(value) {
  const normalized = normalizeOpenAgentTelValue(value);
  if (normalized.length === 0) {
    toast.error(t('aiChatAgentSetup.openAgent.noAvailableChannels', 'No openable deployment channel is connected for this agent.'));
    return;
  }
  closeOpenAgentModal();
  window.location.href = `tel:${normalized}`;
}

function openWhatsappAgentNumber(value) {
  const digits = normalizeOpenAgentPhoneDigits(value);
  if (digits.length === 0) {
    toast.error(t('aiChatAgentSetup.openAgent.missingWhatsappNumber', 'Cannot open WhatsApp: no number is connected.'));
    return;
  }
  closeOpenAgentModal();
  try {
    const safe = new URL(`https://web.whatsapp.com/send?phone=${encodeURIComponent(digits)}`);
    window.open(safe.toString(), '_blank', 'noopener,noreferrer');
  } catch (error) {
  }
}

function closeOpenAgentModal() {
  showOpenAgentModal.value = false;
  openAgentModalChannels.value = [];
  currentOpenAgent.value = null;
}

async function ensureOpenAgentBindings(agent) {
  const cacheKey = getOpenAgentBindingsCacheKey(agent);
  if (cacheKey === '') {
    return { whatsappDisplayNumber: '', phoneDisplayNumber: '' };
  }
  if (openAgentBindingsByAgentId[cacheKey] !== undefined) {
    return openAgentBindingsByAgentId[cacheKey];
  }
  const selectedChannels = getConfiguredDeploymentChannels(agent);
  const nextBindings = { whatsappDisplayNumber: '', phoneDisplayNumber: '' };
  const requests = [];
  if (selectedChannels.includes('Whatsapp') === true || selectedChannels.includes('Whatsapp Voice') === true) {
    requests.push(
      fetchOpenAgentBindingDisplayNumber(`/api/whatsapp/binding/${encodeURIComponent(cacheKey)}`).then(value => {
        nextBindings.whatsappDisplayNumber = value;
      })
    );
  }
  if (selectedChannels.includes('Phone') === true) {
    requests.push(
      fetchOpenAgentBindingDisplayNumber(`/api/phone/binding/${encodeURIComponent(cacheKey)}`).then(value => {
        nextBindings.phoneDisplayNumber = value;
      })
    );
  }
  if (requests.length > 0) {
    await Promise.all(requests);
  }
  openAgentBindingsByAgentId[cacheKey] = nextBindings;
  return nextBindings;
}

function openSelectedAgentChannel(channelId) {
  if (currentOpenAgent.value === null || currentOpenAgent.value === undefined) {
    return;
  }
  if (channelId === 'Web' || channelId === 'Web Voice Agent') {
    openWebAgentChannelForAgent(currentOpenAgent.value);
    return;
  }
  const bindings = getCachedOpenAgentBindings(currentOpenAgent.value);
  if (channelId === 'Whatsapp' || channelId === 'Whatsapp Voice') {
    openWhatsappAgentNumber(bindings.whatsappDisplayNumber);
    return;
  }
  if (channelId === 'Phone') {
    openPhoneAgentNumber(bindings.phoneDisplayNumber);
  }
}

async function openAgentForRecord(agent) {
  const selectedChannels = getConfiguredDeploymentChannels(agent);
  if (selectedChannels.length < 1) {
    navigateToAgent(agent);
    return;
  }
  if (selectedChannels.length === 1 && selectedChannels[0] === 'Web') {
    navigateToAgent(agent);
    return;
  }
  const bindings = await ensureOpenAgentBindings(agent);
  const channels = buildOpenAgentChannels({
    selectedChannels,
    whatsappNumber: bindings.whatsappDisplayNumber,
    phoneNumber: bindings.phoneDisplayNumber,
    t
  });
  if (channels.length === 0) {
    if (selectedChannels.includes('Web') === true || selectedChannels.includes('Web Voice Agent') === true) {
      navigateToAgent(agent);
      return;
    }
    toast.error(t('aiChatAgentSetup.openAgent.noAvailableChannels', 'No openable deployment channel is connected for this agent.'));
    return;
  }
  if (shouldOpenSingleAgentChannelDirectly(channels) === true) {
    currentOpenAgent.value = agent;
    openSelectedAgentChannel(channels[0].id);
    return;
  }
  currentOpenAgent.value = agent;
  openAgentModalChannels.value = channels;
  showOpenAgentModal.value = true;
}

function toggleView(mode) {
  viewMode.value = mode;
  currentPage.value = 1; 
}


function handleSearch(event) {
  const raw = event !== undefined && event !== null && event.target !== undefined && event.target !== null ? event.target.value : '';
  searchQuery.value = sanitizeSearchValue(raw);
}

function autoResize(event) {
  const element = event.target;
  element.style.height = 'auto';
  element.style.height = element.scrollHeight + 'px';
}

function handleChatSubmit() {
  const message = sanitizeChatValue(chatInput.value);
  if (message !== '') {
    router.push({ path: '/chat', query: { initialMessage: message } });
    chatInput.value = '';
  }
}

function sanitizeSearchValue(value) {
  const raw = typeof value === 'string' ? value : '';
  let cleaned = stripControlChars(raw).trim();
  if (cleaned.length > searchMaxLength) {
    cleaned = cleaned.slice(0, searchMaxLength);
  }
  cleaned = cleaned.replace(/\s+/g, ' ');
  return cleaned;
}

function sanitizeChatValue(value) {
  const raw = typeof value === 'string' ? value : '';
  let cleaned = stripControlCharsPreserveWhitespace(raw).trim();
  if (cleaned.length > chatMaxLength) {
    cleaned = cleaned.slice(0, chatMaxLength);
    toast.error('Message is too long');
  }
  return cleaned;
}

function stripControlChars(value) {
  if (typeof value !== 'string') {
    return '';
  }
  let out = '';
  for (let i = 0; i < value.length; i = i + 1) {
    const code = value.charCodeAt(i);
    if (code >= 32 && code !== 127) {
      out = out + value[i];
    }
  }
  return out;
}

function stripControlCharsPreserveWhitespace(value) {
  if (typeof value !== 'string') {
    return '';
  }
  let out = '';
  for (let i = 0; i < value.length; i = i + 1) {
    const code = value.charCodeAt(i);
    if (code === 9 || code === 10 || code === 13) {
      out = out + value[i];
    } else if (code >= 32 && code !== 127) {
      out = out + value[i];
    }
  }
  return out.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function setAgentVisibilityFilter(value) {
  agentVisibilityFilter.value = value;
  updateAvailableAgentsForVisibility();
  currentPage.value = 1;
}

function updateAvailableAgentsForVisibility() {
  if (agentVisibilityFilter.value === 'private') {
    availableAgents.value = allAgents.value.filter(a => a.agenttype === 'PrivateAgent');
  } else if (agentVisibilityFilter.value === 'public') {
    availableAgents.value = allAgents.value.filter(a => a.agenttype === 'PublicAgent');
  } else {
    availableAgents.value = allAgents.value.slice();
  }
}

function normalizeStyleGuid(agent) {
  if (agent.styleguid !== undefined && agent.styleguid !== null && agent.styleguid !== '') {
    return agent.styleguid;
  }
  if (agent.styleGuid !== undefined && agent.styleGuid !== null && agent.styleGuid !== '') {
    return agent.styleGuid;
  }
  if (agent.agentdata !== undefined && agent.agentdata !== null && agent.agentdata.styleGuid !== undefined && agent.agentdata.styleGuid !== null && agent.agentdata.styleGuid !== '') {
    return agent.agentdata.styleGuid;
  }
  return '';
}

function getAgentIcon(agent) {
  const agentId = agent !== undefined && agent !== null && agent.agentid !== undefined && agent.agentid !== null ? String(agent.agentid) : '';
  if (agentId !== '' && styleLogos.value[agentId] !== undefined && styleLogos.value[agentId] !== null && styleLogos.value[agentId] !== '') {
    return styleLogos.value[agentId];
  }
  const guid = normalizeStyleGuid(agent);
  if (guid === '') {
    return '';
  }
  if (styleLogos.value[guid] !== undefined && styleLogos.value[guid] !== null && styleLogos.value[guid] !== '') {
    return styleLogos.value[guid];
  }
  return '';
}

function formatDate(dateString) {
  if (dateString === undefined || dateString === null) {
    return 'Never';
  }
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return 'Recently'; 
  }
  if (hours > 0) {
    return `${hours}h ago`;
  }
  if (minutes > 0) {
    return `${minutes}m ago`;
  }
  return 'Just now';
}

function navigateTo(route) {
  router.push(route);
}

onMounted(async () => {
  updateDateTime();
  setInterval(updateDateTime, 60000);
  loadAvailableAgents();
  loadDashboardStats();
  await nextTick();
  const agentsCard = document.getElementById('dashboard-agents-list-card');
  if (agentsCard !== null && typeof ResizeObserver !== 'undefined') {
    dashboardResizeObserver = new ResizeObserver(() => {
      updateGridCapacity();
    });
    dashboardResizeObserver.observe(agentsCard);
  }
});

async function refreshOrgCssFromIdb() {
  try {
    await initDB();
    const fromIdb = await loadOrgStyles();
    const cssValue = fromIdb !== null && fromIdb !== undefined && typeof fromIdb.css === 'string' ? fromIdb.css : '';
    orgCss.value = cssValue;
  } catch (e) {
    orgCss.value = '';
  }
}

watch(() => globalState.triggerOrgStyleRefresh, (newValue, oldValue) => {
  if (newValue !== null && newValue !== undefined && newValue !== oldValue) {
    refreshOrgCssFromIdb();
  }
});

refreshOrgCssFromIdb();

watch(currentLanguage, () => {
  updateDateTime();
});

watch(viewMode, async () => {
  await nextTick();
  updateGridCapacity();
});

onUnmounted(() => {
  if (dashboardResizeObserver !== null) {
    dashboardResizeObserver.disconnect();
    dashboardResizeObserver = null;
  }
});

const activeAgentsCount = computed(() => {
  if (Array.isArray(availableAgents.value) === true) {
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;
    const count = availableAgents.value.filter(a => {
      if (a.updatedat !== undefined && a.updatedat !== null) {
        const d = new Date(a.updatedat);
        return now.getTime() - d.getTime() <= dayMs;
      }
      return false;
    }).length;
    return count;
  }
  return 0;
});

const timeRangeLabel = computed(() => {
  const found = timeRanges.find(r => r.value === timeRange.value);
  if (found !== undefined && found !== null) {
    return found.label;
  }
  return 'Last 30 days';
});

function toggleRangeMenu() {
  showRangeMenu.value = showRangeMenu.value === true ? false : true;
}

function setTimeRange(value) {
  timeRange.value = value;
  showRangeMenu.value = false;
}

watch(timeRange, () => {
  loadDashboardStats();
});

function formatNumber(value) {
  const n = Number(value);
  if (isNaN(n) === false) {
    if (n >= 1000000) {
      return (n / 1000000).toFixed(1) + 'M';
    }
    if (n >= 1000) {
      return (n / 1000).toFixed(1) + 'K';
    }
    return String(n);
  }
  return '0';
}

const sparklineViewBox = '0 0 96 40';
const sparklineWidth = 96;
const sparklineHeight = 40;
const sparklineAgents = ref([]);
const sparklineConversations = ref([]);
const sparklineMessages = ref([]);
const overviewA = ref([]);
const overviewB = ref([]);
const agentsSparklinePath = computed(() => getSparklinePath(sparklineAgents.value));
const conversationsSparklinePath = computed(() => getSparklinePath(sparklineConversations.value));
const messagesSparklinePath = computed(() => getSparklinePath(sparklineMessages.value));
const overviewSparklinePathA = computed(() => getOverviewPath(overviewA.value));
const overviewSparklinePathB = computed(() => getOverviewPath(overviewB.value));
const trendUp = ref(true);
const trendValue = ref(2.5);
const showTrend = ref(false);
const trendText = computed(() => trendUp.value === true ? t('dashboard.vsPreviousPeriod') : t('dashboard.last30Days'));
const trendLabel = computed(() => `${trendUp.value === true ? '+' : '-'}${trendValue.value}%`);

function buildSparklines() {
  sparklineAgents.value = generateSeries(16, 40, 10);
  sparklineConversations.value = generateSeries(16, 28, 12);
  sparklineMessages.value = generateSeries(16, 24, 14);
  overviewA.value = generateSeries(24, 100, 0);
  overviewB.value = generateSeries(24, 100, 12);

  const current = Number(stats.value.totalMessages);
  const prev = Number(stats.value.previousTotalMessages);
  if (isNaN(current) === false && isNaN(prev) === false) {
    if (prev > 0) {
      const diff = ((current - prev) / prev) * 100;
      const absDiff = Math.abs(diff);
      trendUp.value = diff >= 0;
      trendValue.value = Math.round(absDiff * 10) / 10;
       showTrend.value = trendUp.value === true && trendValue.value >= 1;
    } else {
      showTrend.value = false;
    }
  } else {
    showTrend.value = false;
  }
}

function generateSeries(points, max, phase) {
  const result = [];
  for (let i = 0; i < points; i = i + 1) {
    const v = Math.sin((i + phase) / 2) * 0.5 + 0.5;
    const y = v * max * 0.8 + max * 0.1;
    result.push(y);
  }
  return result;
}

function getSparklinePath(values) {
  if (Array.isArray(values) === false || values.length === 0) {
    return '';
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min === 0 ? 1 : max - min;
  const stepX = sparklineWidth / (values.length - 1);
  let d = '';
  for (let i = 0; i < values.length; i = i + 1) {
    const x = i * stepX;
    const normalized = (values[i] - min) / range;
    const y = sparklineHeight - normalized * sparklineHeight;
    if (i === 0) {
      d = `M ${x} ${y}`;
    } else {
      d = d + ` L ${x} ${y}`;
    }
  }
  return d;
}

function getOverviewPath(values) {
  if (Array.isArray(values) === false || values.length === 0) {
    return '';
  }
  const w = 520;
  const h = 120;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min === 0 ? 1 : max - min;
  const stepX = w / (values.length - 1);
  let d = '';
  for (let i = 0; i < values.length; i = i + 1) {
    const x = i * stepX;
    const normalized = (values[i] - min) / range;
    const y = h - normalized * h;
    if (i === 0) {
      d = `M ${x} ${y}`;
    } else {
      d = d + ` L ${x} ${y}`;
    }
  }
  return d;
}
</script>

<style scoped>
.dashboard-modern {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background-color: #f8fafc;
  overflow: hidden;
}

.dark .dashboard-modern {
  background-color: #020617;
}

.dashboard-modern .modern-panel {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.dark .dashboard-modern .modern-panel {
  background-color: #0f172a;
  border-color: #1e293b;
}

.dashboard-modern .modern-panel:hover {
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.05);
}

.dashboard-modern .agent-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1rem;
  transition: all 0.2s ease;
}

.dark .dashboard-modern .agent-card {
  background-color: #1e293b;
  border-color: #334155;
}

.dashboard-modern .agent-card:hover {
  border-color: #cbd5e1;
  background-color: #ffffff;
}

.dashboard-modern .text-3xl {
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #0f172a;
  font-size: 1.5rem;
}

.dark .dashboard-modern .text-3xl {
  color: #f8fafc;
}

.dashboard-modern .text-gray-500 {
  color: #64748b;
  font-weight: 500;
}

.dashboard-modern .bg-amber-50 {
  background-color: #fffbeb;
  border-radius: 12px;
}

.dashboard-modern .text-amber-600 {
  color: #d97706;
}

.dashboard {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}


.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}


.group:hover {
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}


.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

.font-host-grotesk {
  font-family: "Host Grotesk", sans-serif !important;
}
.font-heebo {
  font-family: "Heebo", sans-serif !important;
}
.dashboard-card {
  box-shadow: 0 2px 6px rgba(2, 6, 23, 0.03);
}
.modern-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.12s ease, border-color 0.12s ease;
}
.modern-panel:hover {
  border-color: #d9dfe8;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.05);
}
.agent-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}
.agent-card:hover {
  border-color: #cbd5e1;
  background: #ffffff;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.05);
}
.action-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.12s ease, border-color 0.12s ease, background 0.12s ease;
}
.action-tile:hover {
  border-color: #cbd5e1;
  background: #ffffff;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.05);
}
#dashboard-main-container {
  flex: 1 1 0%;
  height: auto;
  min-height: 0;
  margin-bottom: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}
#dashboard-header-section,
#dashboard-stats-cards-section,
#dashboard-stats-cards-section-skeleton {
  flex-shrink: 0;
}
#dashboard-main-content-area {
  flex: 1 1 0%;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  grid-template-rows: minmax(0, 1fr);
}
#dashboard-agents-list-container {
  min-height: 0;
}
#dashboard-agents-list-card {
  min-height: 0;
  height: 100%;
  overflow: hidden;
}
#dashboard-agents-scrollable-list {
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-bottom: 0.5rem;
  container-type: inline-size;
}
#dashboard-agents-scrollable-list :deep(#modern-agent-grid) {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  height: auto;
  min-height: 0;
  width: calc(100% + 1rem);
  padding: 0;
  align-content: start;
}
#dashboard-agents-scrollable-list :deep(#modern-agent-grid > .modern-agent-card) {
  box-sizing: border-box;
  flex: none !important;
  width: auto !important;
  min-width: 0;
  height: auto;
}
@container (min-width: 520px) {
  #dashboard-agents-scrollable-list :deep(#modern-agent-grid) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
#dashboard-agents-scrollable-list :deep(.modern-agent-card .card-inner) {
  box-sizing: border-box;
  min-width: 0;
  padding: 1rem;
  min-height: 0;
}
#dashboard-agents-scrollable-list :deep(.modern-agent-card .card-content) {
  min-width: 0;
  overflow: hidden;
}
#dashboard-agents-scrollable-list :deep(.modern-agent-card .agent-name) {
  min-width: 0;
  max-width: 100%;
  overflow: hidden !important;
  text-overflow: ellipsis;
  white-space: normal !important;
  overflow-wrap: anywhere;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
#dashboard-agents-scrollable-list :deep(.modern-agent-card .card-header) {
  margin-bottom: 0.75rem;
}
#dashboard-agents-scrollable-list :deep(.modern-agent-card .agent-desc) {
  margin-bottom: 0.75rem;
  overflow-wrap: anywhere;
}
#dashboard-agents-scrollable-list :deep(.modern-agent-card .card-footer) {
  min-width: 0;
  padding-top: 0.75rem;
}
#dashboard-agents-grid-view {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-content: start;
  width: calc(100% + 1rem);
  height: auto;
  padding: 0;
}
#dashboard-agents-grid-view .agent-card {
  box-sizing: border-box;
  flex: none;
  width: auto;
  min-width: 0;
  height: auto;
  padding: 0.75rem;
  overflow: hidden;
}
#dashboard-agents-grid-view .agent-card > div,
#dashboard-agents-grid-view .agent-card > div > div {
  min-width: 0;
}
#dashboard-agents-grid-view .agent-card h3,
#dashboard-agents-grid-view .agent-card p {
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
  white-space: normal;
}
#dashboard-agents-grid-view .agent-card h3 {
  display: -webkit-box;
  font-size: 0.875rem;
  line-height: 1.25;
  line-clamp: 1;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}
#dashboard-agents-grid-view .agent-card p {
  display: -webkit-box;
  font-size: 0.6875rem;
  line-height: 1.25;
  line-clamp: 1;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}
#dashboard-agents-grid-view .agent-card button {
  padding: 0.25rem 0.5rem;
  font-size: 0.625rem;
}
@container (min-width: 520px) {
  #dashboard-agents-grid-view {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
#dashboard-sidebar-actions-and-activity {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  align-items: stretch;
}
#dashboard-performance-section,
#dashboard-usage-section {
  min-height: 0;
  height: auto;
  align-self: start;
}
#dashboard-usage-section :deep(.modern-usage-container) {
  height: auto;
  min-height: 0;
}
#dashboard-quick-actions-panel {
  width: 100%;
  min-height: 0;
  margin-top: 1rem;
  overflow: hidden;
}
#dashboard-quick-actions-panel :deep(#dashboard-quick-actions-section) {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  min-height: 0;
  height: auto;
}
#dashboard-quick-actions-panel :deep(#dashboard-quick-actions-grid) {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  width: 100%;
}
#dashboard-quick-actions-panel :deep(#dashboard-quick-actions-grid > button) {
  width: 100%;
  padding: 0.25rem;
}
@media (min-width: 640px) {
  #dashboard-quick-actions-panel :deep(#dashboard-quick-actions-grid) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (min-width: 1024px) {
  #dashboard-agents-list-container {
    padding-inline-end: 0.75rem;
  }
  #dashboard-sidebar-actions-and-activity {
    padding-inline-start: 0.75rem;
  }
}
#dashboard-quick-chat-section {
  min-height: 220px;
}
</style>
